import { useRef } from "react";
import { FieldGroup } from "@components/ui/field";
import AdCampaignAssetInput from "../shared/AdCampaignAssetInput";
import AdCampaignFieldList from "../shared/AdCampaignFieldList";
import { useAssetForm } from "@components/ads/hooks/useAssetForm";
import { Ads } from "@shared";
import type { UpdateCampaignRequestBody } from "@rails_api_base";
import { z } from "zod";

const keywordTextSchema = z.string().min(1, "Keyword cannot be empty").max(80, "Maximum 80 characters");

export default function NegativeKeywordTargetingForm() {
  const gridEndRef = useRef<HTMLDivElement>(null);

  const { addAsset, removeAsset, updateAsset, lockToggle, fields, isPlaceholder, methods, control } =
    useAssetForm({
      assetKey: "negative_keywords",
      formGroup: "negative_keywords",
      schema: Ads.NegativeKeywordsFormSchema,
      // Submit negatives as part of the single `keywords` collection with `negative: true`.
      // The backend partitions by the flag, so this won't touch positives.
      transformFn: (data): Partial<UpdateCampaignRequestBody> | null => {
        const transformed = data.negative_keywords
          ?.filter((k: Ads.Asset) => k.text?.trim())
          .map(({ id, text, locked }: Ads.Asset) => ({
            id,
            text,
            match_type: "broad",
            locked: locked ?? false,
            negative: true,
          }));

        if (!transformed || transformed.length === 0) return null;
        return { keywords: transformed as unknown as UpdateCampaignRequestBody["keywords"] };
      },
    });

  const handleAddKeyword = (value: string) => {
    addAsset(value);

    setTimeout(() => {
      const element = gridEndRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const paginationHeight = 80;
      const viewportBottom = window.innerHeight - paginationHeight;

      if (rect.bottom > viewportBottom) {
        window.scrollBy({
          top: rect.bottom - viewportBottom + 16,
          behavior: "smooth",
        });
      }
    }, 0);
  };

  const leftColumnFields = fields.filter((_, i) => i % 2 === 0);
  const rightColumnFields = fields.filter((_, i) => i % 2 === 1);
  const resolveIndex = (id: string) => fields.findIndex((f) => f.id === id);

  return (
    <FieldGroup className="gap-4">
      <AdCampaignAssetInput
        label="Keywords to exclude"
        onAdd={handleAddKeyword}
        currentCount={fields.length}
        maxCount={Ads.NEGATIVE_KEYWORDS_MAX}
        placeholder="Enter terms to exclude, separated by commas"
        maxLength={80}
        validationSchema={keywordTextSchema}
        badgeText={`Up to ${Ads.NEGATIVE_KEYWORDS_MAX}`}
        tooltipText="Negative keywords prevent your ad from showing when these terms appear in the search. Use them to filter out irrelevant traffic (e.g., 'free', 'jobs', 'diy')."
        error={(methods.formState.errors as { negative_keywords?: { message?: string } }).negative_keywords?.message}
      />
      {fields.length > 0 && (
        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <div className="flex flex-col gap-4">
            <AdCampaignFieldList
              fieldName="negative_keywords"
              fields={leftColumnFields as any}
              onLockToggle={lockToggle}
              onDelete={removeAsset}
              control={control as any}
              placeholder="Excluded Keyword"
              maxLength={80}
              resolveIndex={resolveIndex}
              onInputChange={updateAsset}
              isPlaceholder={isPlaceholder}
            />
          </div>
          <div className="flex flex-col gap-4">
            <AdCampaignFieldList
              fieldName="negative_keywords"
              fields={rightColumnFields as any}
              onLockToggle={lockToggle}
              onDelete={removeAsset}
              control={control as any}
              placeholder="Excluded Keyword"
              maxLength={80}
              resolveIndex={resolveIndex}
              onInputChange={updateAsset}
              isPlaceholder={isPlaceholder}
            />
          </div>
          <div ref={gridEndRef} />
        </div>
      )}
    </FieldGroup>
  );
}
